import { Card, EmptyState, Icon, Typography, type LucideIconName } from "@/deps/ui-library";
import { Link, useParams } from "@tanstack/react-router";

const FEATURES: Record<
  string,
  { title: string; description: string; icon: LucideIconName; wave: string }
> = {
  "year-end": {
    title: "Year-end close",
    description: "Checks, adjustments, a preview, approval and the lock, with a close log.",
    icon: "CalendarCheck",
    wave: "Plan slice 8",
  },
  compliance: {
    title: "Compliance",
    description: "Legally-ready checks and BIS 3.0 validation.",
    icon: "ShieldCheck",
    wave: "Deferred",
  },
  quotations: {
    title: "Quotations",
    description: "Quotes become invoices in one step and keep their own QT sequence.",
    icon: "FilePenLine",
    wave: "Wave 2 · see design/Quotations.dc.html",
  },
  customers: {
    title: "Customers",
    description: "Parties, statements and receivable analysis.",
    icon: "Users",
    wave: "Wave 2 · see design/Customers.dc.html",
  },
  "purchase-orders": {
    title: "Purchase orders",
    description: "Commitments that turn into bills.",
    icon: "ClipboardList",
    wave: "Wave 2 · see design/Purchase Orders.dc.html",
  },
  suppliers: {
    title: "Suppliers",
    description: "Parties, statements and payable analysis.",
    icon: "Building2",
    wave: "Wave 2 · see design/Suppliers.dc.html",
  },
  banking: {
    title: "Bank accounts",
    description: "Balances, feeds and reconciliation.",
    icon: "Landmark",
    wave: "Wave 2 · see design/Banking.dc.html",
  },
  journals: {
    title: "Journals",
    description: "Manual vouchers with balanced debit and credit lines.",
    icon: "BookOpen",
    wave: "Wave 2",
  },
  employees: {
    title: "Employee ledgers",
    description: "Advances, claims and settlements per employee.",
    icon: "Users",
    wave: "Wave 2 · see design/Employees.dc.html",
  },
  reports: {
    title: "Reports",
    description: "Every financial statement, the VAT and TDS returns, and the reporting layer.",
    icon: "ChartColumn",
    wave: "Wave 2 · see design/Reports.dc.html",
  },
  "invoice-editor": {
    title: "New invoice",
    description: "The BIS 3.0 invoice editor.",
    icon: "FileText",
    wave: "Wave 2 · see design/Invoice Editor (BIS 3.0).dc.html",
  },
  integrations: {
    title: "Integrations",
    description: "Rigo Billing and RigoHR connections, sync health.",
    icon: "Link2",
    wave: "Wave 3 · see design/Integrations.dc.html",
  },
};

/** Design: "Planned screen" stub (lines 2299–2309) for nav targets owned by other design files. */
export function PlannedFeaturePage() {
  const { feature } = useParams({ strict: false }) as { feature?: string };
  const f = (feature && FEATURES[feature]) || {
    title: "Planned",
    description: "This area is designed and scheduled for a later wave.",
    icon: "Sparkles",
    wave: "Later wave",
  };
  return (
    <section>
      <Card variant="outlined" size="none">
        <EmptyState.Root config={{ size: "sm" }}>
          <EmptyState.Content config={{ size: "sm" }}>
            <EmptyState.Indicator config={{ size: "sm", variant: "primary" }}>
              <Icon name={f.icon} size="md" />
            </EmptyState.Indicator>
            <div className="flex flex-col gap-1">
              <EmptyState.Title config={{ size: "sm" }}>{f.title}</EmptyState.Title>
              <EmptyState.Description config={{ size: "sm" }}>
                {f.description}
              </EmptyState.Description>
              <Typography.Text as="p" variant="small" muted className="num">
                {f.wave}
              </Typography.Text>
            </div>
            <EmptyState.Actions config={{ size: "sm" }}>
              <Link to="/" className="text-sm text-primary font-medium">
                Back to overview
              </Link>
            </EmptyState.Actions>
          </EmptyState.Content>
        </EmptyState.Root>
      </Card>
    </section>
  );
}
